/**
 * Two separate axios clients, because this app talks to two separate
 * backends (see docs/tis-runs/tenant-platform-buildout/contracts/
 * tenant-platform-api-contracts.md):
 *
 *  - `tenantApi` — this platform's own tis-tenant-platform-service
 *    (plans, signup, subscriptions, invoices, platform dashboard).
 *  - `authApi` — the central tis-auth-service (login, token refresh).
 *    Login/refresh are owned by Auth per tis-requirements.md
 *    ("centralized authn and authz under tis-auth should be used for
 *    all") — this app never mints or validates tokens itself, it only
 *    attaches the access token Auth already issued.
 *
 * Mirrors tis-spa-talent's src/lib/api.js interceptor pattern (token
 * attach on request, refresh-and-retry once on 401), adapted for two
 * clients instead of one.
 */
import axios from "axios";

const TENANT_API_URL = process.env.REACT_APP_TENANT_API_URL;
const AUTH_API_URL = process.env.REACT_APP_AUTH_API_URL;
const AUTH_TENANT_SERVICE_URL = process.env.REACT_APP_AUTH_TENANT_SERVICE_URL;
const RBAC_API_URL = process.env.REACT_APP_RBAC_API_URL;
const IAM_API_URL = process.env.REACT_APP_IAM_API_URL;

export const tenantApi = axios.create({ baseURL: `${TENANT_API_URL}/api/v1` });
export const authApi = axios.create({ baseURL: `${AUTH_API_URL}/api/v1` });
// tis-auth-tenant-service's own public API (identity/lifecycle/branding —
// see contracts doc section 1). Called directly for tenant resolution and
// signup polling, exactly like tis-spa-talent/tis-spa-hub already do —
// not proxied through Tenant/backend, which does not own this data.
export const authTenantServiceApi = axios.create({ baseURL: AUTH_TENANT_SERVICE_URL });
// tis-auth-rbac-service (roles/permissions, GET /api/v1/roles on :3004) —
// the Auth platform's authorization authority. Roles are NOT served by
// auth-service (login) or tenant-service; this is their own service.
export const rbacApi = axios.create({ baseURL: `${RBAC_API_URL}/api/v1` });
// tis-auth-iam-service (user CRUD — GET/POST /users, PATCH /users/:id,
// PATCH /users/:id/status, POST /users/:id/resend-activation; dev :3002).
// The identity authority for tenant users — distinct from auth-service
// (login) and rbac-service (roles). The SPA did not talk to it before the
// Users directory on the Users & Roles page needed it.
export const iamApi = axios.create({ baseURL: `${IAM_API_URL}/api/v1` });

function attachToken(config) {
  const token = localStorage.getItem("tenant_platform_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // tenant-service / rbac-service derive tenant from the JWT's `tid`
  // claim, but attaching the header too is harmless and matches the
  // service-to-service convention; some read routes still read it.
  const tenantId = localStorage.getItem("tenant_platform_tenant_id");
  if (tenantId && !config.headers["x-tis-tenant-id"]) config.headers["x-tis-tenant-id"] = tenantId;
  return config;
}
// Every client that calls a token-protected Auth/tenant endpoint needs the
// access token attached. authApi login/refresh work without one (public),
// but its other routes and authTenantServiceApi/rbacApi all require it —
// without this, /configuration and /roles return 401 "Missing or
// malformed Authorization header".
tenantApi.interceptors.request.use(attachToken);
authApi.interceptors.request.use(attachToken);
authTenantServiceApi.interceptors.request.use(attachToken);
rbacApi.interceptors.request.use(attachToken);
iamApi.interceptors.request.use(attachToken);

function clearSessionAndRedirect() {
  localStorage.removeItem("tenant_platform_token");
  localStorage.removeItem("tenant_platform_refresh_token");
  localStorage.removeItem("tenant_platform_tenant_id");
  localStorage.removeItem("tenant_platform_principal_kind");
  if (window.location.pathname !== "/login") window.location.href = "/login";
}

let refreshPromise = null;

export async function refreshAccessToken() {
  if (!refreshPromise) {
    const tenantId = localStorage.getItem("tenant_platform_tenant_id") || "";
    refreshPromise = authApi
      .post(
        "/auth/token/refresh",
        { refresh_token: localStorage.getItem("tenant_platform_refresh_token") },
        { headers: { "x-tis-tenant-id": tenantId } },
      )
      .then((res) => {
        localStorage.setItem("tenant_platform_token", res.data.access_token);
        localStorage.setItem("tenant_platform_refresh_token", res.data.refresh_token);
        return res.data.access_token;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

/**
 * Install a 401 → refresh-token → retry-once interceptor on an axios
 * client. On a 401 (e.g. the access token expired while the tab was
 * open, or on a page reload), transparently mint a fresh access token
 * with the stored refresh token and replay the ORIGINAL request on the
 * SAME client — the user never sees "Access token has expired".
 *
 * LOGOUT POLICY (hardened): we clear the session and redirect to /login
 * in exactly ONE case — the refresh ITSELF fails (no refresh token, or
 * it's expired/invalid), which means the session really is dead. A 401
 * that comes back on a SPECIFIC request AFTER a successful refresh is a
 * data/authz 401 (a mispointed service seam, a per-endpoint permission
 * or audience quirk), NOT a dead session — so it is surfaced to the
 * page's own error handling instead of nuking the session. This is the
 * fix for the "clicking a page bounces me to /login" loop, where one
 * authed data call 401'd and the blanket interceptor logged the user
 * out before the page could show a readable error. Concurrent 401s
 * share one in-flight refresh via refreshPromise.
 *
 * Registered on EVERY authed client (tenantApi, authTenantServiceApi,
 * rbacApi, iamApi) so the behaviour is uniform across all backends.
 */
function installRefreshRetry(client) {
  client.interceptors.response.use(
    (res) => res,
    async (err) => {
      if (err.response?.status !== 401) return Promise.reject(err);

      // A 401 that comes back AFTER we already refreshed-and-retried is
      // NOT an auth failure: the refresh succeeded, so the session/token
      // is valid — this endpoint is denying the specific request (a
      // mispointed seam, a data/authz 401 on one call). Nuking the whole
      // session for that is the "clicking X bounces to /login" bug. So
      // surface it to the page's own error handling instead of logging
      // the user out.
      if (err.config._retriedAfterRefresh) {
        return Promise.reject(err);
      }

      // No refresh token at all -> genuinely unauthenticated -> log out.
      const refreshToken = localStorage.getItem("tenant_platform_refresh_token");
      if (!refreshToken) {
        clearSessionAndRedirect();
        return Promise.reject(err);
      }

      try {
        const newToken = await refreshAccessToken();
        err.config._retriedAfterRefresh = true;
        err.config.headers = err.config.headers || {};
        err.config.headers.Authorization = `Bearer ${newToken}`;
        return client(err.config);
      } catch (refreshErr) {
        // The refresh ITSELF failed (refresh token expired/invalid) -> the
        // session really is dead -> this is the one place we log out.
        clearSessionAndRedirect();
        return Promise.reject(err);
      }
    },
  );
}

installRefreshRetry(tenantApi);
installRefreshRetry(authTenantServiceApi);
installRefreshRetry(rbacApi);
installRefreshRetry(iamApi);

/**
 * Platform-superadmin IRREVERSIBLE tenant hard-delete. Calls the
 * tis-tenant-platform-service P-SA route DELETE /platform/tenants/:id, which
 * (server-side, with the internal service secret) forwards to the purge
 * orchestrator a browser can never reach directly. Returns the per-service
 * deletion report { tenant_id, ok, services:{<svc>:{deleted|error}} }.
 */
export async function deletePlatformTenant(tenantId) {
  const res = await tenantApi.delete(`/platform/tenants/${tenantId}`);
  return res.data;
}

/** Status-code → friendly, end-user copy. No HTTP numbers, no stack, no
 * jargon. These are the fallback when the backend did not send a message
 * we can safely show a user. */
const STATUS_MESSAGE = {
  400: "Some of the information looks incorrect. Please review and try again.",
  401: "Your session has expired. Please sign in again.",
  403: "You don't have permission to do that.",
  404: "We couldn't find what you were looking for.",
  409: "That change conflicts with the current state. Refresh and try again.",
  413: "That file or request is too large.",
  422: "Some of the information looks incorrect. Please review and try again.",
  429: "You're going a bit fast — please wait a moment and try again.",
  500: "Something went wrong on our end. Please try again shortly.",
  502: "The service is temporarily unavailable. Please try again shortly.",
  503: "The service is temporarily unavailable. Please try again shortly.",
  504: "The request took too long. Please try again shortly.",
};

const GENERIC = "Something went wrong. Please try again.";
const OFFLINE = "We couldn't reach the server. Check your connection and try again.";

/** A backend string is only shown to the user when it reads like a real,
 * human sentence — not a raw technical token (Network Error, ECONNREFUSED,
 * a URL, a stack frame, a bare error class). This is the guard that stops
 * "Network Error" / "Invalid error" from ever reaching the UI. */
function isHumanReadable(msg) {
  if (typeof msg !== "string") return false;
  const s = msg.trim();
  if (s.length < 3 || s.length > 300) return false;
  if (/^network error$/i.test(s)) return false;
  if (/\b(ECONN|ETIMEDOUT|ENOTFOUND|ERR_|xhr|axios|undefined|null|NaN)\b/i.test(s)) return false;
  if (/^https?:\/\//i.test(s) || /\bat .+:\d+:\d+/.test(s)) return false; // url / stack frame
  if (/^[A-Z][a-zA-Z]*Error$/.test(s)) return false; // bare "TypeError" etc.
  return /[a-zA-Z]/.test(s) && /\s|[.!?]/.test(s); // looks like a phrase
}

/** True when an axios error is a benign request cancellation (navigation
 * away, component unmount, StrictMode double-mount) rather than a real
 * backend failure. Callers should NOT show an error banner for these. */
export function isCanceled(err) {
  return (
    err?.code === "ERR_CANCELED" ||
    err?.name === "CanceledError" ||
    err?.name === "AbortError" ||
    err?.message === "canceled"
  );
}

/**
 * Normalize ANY thrown axios error into a single friendly, end-user
 * sentence — never a technical string. Pass the WHOLE error (err).
 * Returns null for a canceled request so callers skip the banner.
 *
 * Order: canceled -> no-response (network/CORS/timeout) -> human-readable
 * backend message -> status-code copy -> generic. A validation array
 * (422) is summarized to the first field message when that reads cleanly.
 */
export function errorMessage(err) {
  if (isCanceled(err)) return null;

  // No response at all: offline, DNS, connection refused, CORS-blocked, or
  // a timeout. axios reports these as "Network Error"/ECONNABORTED — never
  // show that raw. (A CORS preflight failure also lands here.)
  if (err && !err.response) {
    if (err.code === "ECONNABORTED" || /timeout/i.test(err.message || "")) {
      return "The request took too long. Please try again.";
    }
    return OFFLINE;
  }

  const status = err?.response?.status;
  const data = err?.response?.data;

  // Prefer a backend-sent message, but ONLY if it is human-readable.
  const raw = data?.message ?? data?.detail ?? data?.error;
  if (Array.isArray(raw)) {
    const first = raw.find((e) => isHumanReadable(e?.msg) || isHumanReadable(e));
    const picked = first?.msg ?? first;
    if (isHumanReadable(picked)) return picked;
  } else if (isHumanReadable(raw)) {
    return raw;
  } else if (isHumanReadable(data)) {
    return data;
  }

  // Fall back to friendly copy for the status code.
  if (status && STATUS_MESSAGE[status]) return STATUS_MESSAGE[status];
  if (status >= 500) return STATUS_MESSAGE[500];
  return GENERIC;
}

/** Low-level extractor for a RAW detail value (not an axios error) — used
 * where a value is already unwrapped. Prefer errorMessage(err) for thrown
 * errors; this stays for backward compatibility. */
export function apiError(detail) {
  if (detail == null) return GENERIC;
  if (isHumanReadable(detail)) return detail;
  if (Array.isArray(detail)) {
    const first = detail.find((e) => isHumanReadable(e?.msg) || isHumanReadable(e));
    if (first) return first.msg ?? first;
  }
  if (isHumanReadable(detail?.message)) return detail.message;
  if (isHumanReadable(detail?.msg)) return detail.msg;
  return GENERIC;
}
